import { useState } from 'react';
import { ArrowRight, Check, ChevronDown, EyeOff, Gauge, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useLocalPotential } from '@/hooks/useLocalPotential';
import { useLocalPotentialActions } from '@/hooks/useLocalPotentialActions';
import { hrefFor, isSelfAttested, presentationFor } from '@/data/localPotential';

/**
 * First-30-days quick wins (NEA-5870) — the "Your first 30 days" right column.
 * A curated, status-gated action queue from `useLocalPotential().quickWins` (the
 * same compute-score resolver as the score; channels never appear here, only the
 * doc's curated set). Completion is derived server-side, so a win disappears and
 * the next slides in the moment the underlying state changes. Hide/restore
 * persists per-shop in `dismissedHints` (namespaced `quickwin:*`).
 *
 * Rendered as the design's compact right-hand column (own header + scroll), so it
 * drops straight into the Overview grid's third cell.
 */
function Header() {
	return (
		<div className="flex items-center gap-2.5 px-1">
			<div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-unify text-white">
				<Gauge className="h-4 w-4" />
			</div>
			<div className="min-w-0">
				<div className="text-sm font-bold leading-tight text-foreground">Quick wins</div>
				<div className="text-[11px] leading-tight text-muted-foreground">Easy boosts to grow your local potential</div>
			</div>
		</div>
	);
}

// make-products-visible carries the live N-of-M count; everything else uses its
// static presentation title.
const titleFor = (id, productVisible) => {
	const base = presentationFor(id).title;
	if (id === 'make-products-visible' && productVisible && productVisible.total > 0) {
		const missing = Math.max(productVisible.total - productVisible.visible, 0);
		return `Make ${missing.toLocaleString('en-GB')} (of ${productVisible.total.toLocaleString('en-GB')}) in-stock products visible`;
	}
	return base;
};

export function QuickWinsStack() {
	const { shop } = useShop();
	const { quickWins, productVisible, isLoading } = useLocalPotential();
	const { claimWin, dismiss, restore } = useLocalPotentialActions();
	const { shopPath } = useShopPath();
	const [trayOpen, setTrayOpen] = useState(false);

	if (isLoading && !quickWins) {
		return (
			<div className="flex flex-col gap-3 md:h-[320px]">
				<Header />
				<Skeleton className="h-16 rounded-2xl" />
				<Skeleton className="h-16 rounded-2xl" />
			</div>
		);
	}

	const wins = quickWins || [];
	const hiddenIds = (shop?.dismissedHints || []).filter((id) => id.startsWith('quickwin:')).map((id) => id.slice('quickwin:'.length));

	return (
		<div className="flex flex-col gap-3 md:h-[320px]">
			<Header />
			<div className="-mr-2 flex min-h-0 flex-1 flex-col overflow-y-auto pr-2">
				{wins.length === 0 && hiddenIds.length === 0 ? (
					<div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/30 p-6 text-center">
						<div className="text-sm font-semibold text-foreground">Great work! All quick wins done</div>
						<div className="mt-1 text-xs text-muted-foreground">New suggestions appear as your store grows.</div>
						<Link
							to={shopPath('/local-potential')}
							className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gradient-unify px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:opacity-90"
						>
							Improve my store <ArrowRight className="h-3 w-3" />
						</Link>
					</div>
				) : null}

				{wins.map((item) => {
					const p = presentationFor(item.id);
					const Icon = p.icon;
					const selfAttested = isSelfAttested(item.id);
					return (
						<div
							key={item.id}
							className="group mb-2 flex items-start gap-2.5 rounded-2xl border border-border/60 bg-white p-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all last:mb-0 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_rgba(79,70,229,0.25)]"
						>
							<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
								<Icon className="h-3.5 w-3.5" />
							</div>
							<Link to={shopPath(hrefFor(item))} className="min-w-0 flex-1 outline-none">
								<div className="break-words text-sm font-semibold leading-snug text-foreground">
									{titleFor(item.id, productVisible)}
								</div>
								<div className="mt-0.5 break-words text-xs text-muted-foreground">
									{p.body}
									{item.uplift ? <span className="font-semibold text-primary"> +{item.uplift}%</span> : null}
								</div>
							</Link>
							<button
								type="button"
								onClick={() => dismiss(`quickwin:${item.id}`)}
								aria-label={`Hide "${p.title}"`}
								title="Hide"
								className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border/80 bg-white text-muted-foreground transition hover:border-foreground hover:text-foreground"
							>
								<EyeOff className="h-3.5 w-3.5" />
							</button>
							{selfAttested ? (
								<button
									type="button"
									onClick={() => claimWin(item.id)}
									aria-label={`Mark "${p.title}" as done`}
									title="Mark done"
									className="-ml-1.5 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition hover:opacity-90"
								>
									<Check className="h-3.5 w-3.5" />
								</button>
							) : (
								<Link
									to={shopPath(hrefFor(item))}
									aria-label={`Open ${p.title}`}
									className="-ml-1.5 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition hover:opacity-90"
								>
									<span aria-hidden="true" className="text-sm font-bold leading-none">
										?
									</span>
								</Link>
							)}
						</div>
					);
				})}

				{hiddenIds.length > 0 ? (
					<div className="pt-1">
						<button
							type="button"
							onClick={() => setTrayOpen((v) => !v)}
							aria-expanded={trayOpen}
							className="inline-flex w-full items-center justify-between gap-2 rounded-xl border border-dashed border-border bg-accent/30 px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:bg-accent hover:text-foreground"
						>
							<span className="inline-flex items-center gap-2">
								<EyeOff className="h-3.5 w-3.5" /> {hiddenIds.length} hidden suggestion{hiddenIds.length === 1 ? '' : 's'}
							</span>
							<span className="inline-flex items-center gap-1">
								{trayOpen ? 'Hide' : 'Show'}
								<ChevronDown className={`h-3.5 w-3.5 transition-transform ${trayOpen ? 'rotate-180' : ''}`} />
							</span>
						</button>
						{trayOpen ? (
							<div className="mt-2 flex flex-col opacity-90">
								{hiddenIds.map((id) => {
									const p = presentationFor(id);
									const Icon = p.icon;
									return (
										<div
											key={id}
											className="mb-2 flex items-start gap-2.5 rounded-2xl border border-border/60 bg-white p-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] last:mb-0"
										>
											<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
												<Icon className="h-3.5 w-3.5" />
											</div>
											<div className="min-w-0 flex-1">
												<div className="break-words text-sm font-semibold leading-snug text-foreground">
													{titleFor(id, productVisible)}
												</div>
												<div className="mt-0.5 break-words text-xs text-muted-foreground">{p.body}</div>
											</div>
											<button
												type="button"
												onClick={() => restore(`quickwin:${id}`)}
												aria-label={`Unhide "${p.title}"`}
												title="Unhide"
												className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border/80 bg-white text-muted-foreground transition hover:border-foreground hover:text-foreground"
											>
												<RotateCcw className="h-3.5 w-3.5" />
											</button>
										</div>
									);
								})}
							</div>
						) : null}
					</div>
				) : null}
			</div>
		</div>
	);
}
